import 'react';
import classnames from 'classnames';
import styled, { keyframes, useTheme } from 'styled-components';
import { selectFromTheme, stylesFromMap } from '@thisisbud/sc-utils';
import { jsx, jsxs } from 'react/jsx-runtime';
import { Padder } from '@thisisbud/bui-react-padder';
import { H4 } from '@thisisbud/bui-react-typography';

let _$1 = t => t,
  _t$1;
const progressBarClassName = 'bui-progress-bar';

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledProgress$1 = /*#__PURE__*/styled.progress.withConfig({
  displayName: "StyledProgress",
  componentId: "sc-1nzv84q-0"
})(_t$1 || (_t$1 = _$1`
  display: block;
  appearance: none;
  border: none;
  width: 100%;
  overflow: hidden;
  background-color: ${0};
  border-radius: ${0};
  color: ${0};
  height: ${0};

  &::-webkit-progress-bar {
    background-color: ${0};
    border-radius: ${0};
  }

  &::-webkit-progress-value {
    background-color: ${0};
    border-radius: ${0};
  }

  &::-moz-progress-bar {
    background-color: ${0};
    border-radius: ${0};
  }

  .bui-file-name + & {
    margin-top: ${0};
  }
`), selectFromTheme('colorBackgroundProgressBar'), selectFromTheme('borderRadiusProgressBarDefault'), selectFromTheme('colorProgressBar'), selectFromTheme('thicknessProgressBarDefault'), selectFromTheme('colorBackgroundProgressBar'), selectFromTheme('borderRadiusProgressBarDefault'), selectFromTheme('colorProgressBar'), selectFromTheme('borderRadiusProgressBarDefault'), selectFromTheme('colorProgressBar'), selectFromTheme('borderRadiusProgressBarDefault'), selectFromTheme('spacingXXSmallDefault'));

/**
 * Displays the current progress of an action as a progress bar.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { ProgressBar } from '@thisisbud/bui-react-progress'
 *
 * function Example() {
 *   return <ProgressBar value={25} max={100}/>
 * }
 * ```
 */
function ProgressBar(props) {
  const {
    className,
    id,
    max,
    value
  } = props;
  return /*#__PURE__*/jsx(StyledProgress$1, {
    className: classnames(progressBarClassName, className),
    id: id,
    max: max,
    value: value
  });
}
ProgressBar.defaultProps = {
  max: 1
};
ProgressBar.displayName = 'ProgressBar';

let _ = t => t,
  _t,
  _t2,
  _t3,
  _t4,
  _stop;
const progressSpinnerClassName = 'bui-progress-spinner';
const spinnerColorSelector = selectFromTheme('colorSpinner');

/**
 * Get the color of the spinner.
 *
 * @param theme - The StyledComponents theme
 */
function getSpinnerColor(theme) {
  return spinnerColorSelector({
    theme
  });
}
const rotateAnimation = /*#__PURE__*/keyframes(_t || (_t = _`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`));

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledPadder = /*#__PURE__*/styled(Padder).withConfig({
  displayName: "StyledPadder",
  componentId: "sc-1ccnvrw-0"
})(_t2 || (_t2 = _`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`));

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledProgress = /*#__PURE__*/styled.svg.withConfig({
  displayName: "StyledProgress",
  componentId: "sc-1ccnvrw-1"
})(_t3 || (_t3 = _`
  ${0}
  animation: ${0} 0.8s linear infinite;

  @media (prefers-reduced-motion) {
    animation: ${0} 4s linear infinite;
  }
`), stylesFromMap({
  height: selectFromTheme('sizeSpinnerDefault'),
  width: selectFromTheme('sizeSpinnerDefault')
}), rotateAnimation, rotateAnimation);

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledH4 = /*#__PURE__*/styled(H4).withConfig({
  displayName: "StyledH4",
  componentId: "sc-1ccnvrw-2"
})(_t4 || (_t4 = _`
  margin-top: ${0};
`), selectFromTheme('spacingXLargeDefault'));

/**
 * Displays underteminate progress of an action as a spinner.
 *
 * @see https://w3c.github.io/aria/#progressbar
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { ProgressSpinner } from '@thisisbud/bui-react-progress'
 *
 * function Example() {
 *   return <ProgressSpinner id='spinner'>Please wait while the page loads</ProgressSpinner>
 * }
 * ```
 */
function ProgressSpinner(props) {
  const {
    children,
    className,
    id
  } = props;
  const theme = /*#__PURE__*/useTheme();
  const labelId = `${id}-label`;
  const spinnerGradientId = `${id}-spinner-gradient`;
  return /*#__PURE__*/jsxs(StyledPadder, {
    className: classnames(progressSpinnerClassName, className),
    children: [/*#__PURE__*/jsxs(StyledProgress, {
      "aria-labelledby": labelId,
      height: "100",
      id: id,
      role: "progressbar",
      width: "100",
      children: [/*#__PURE__*/jsxs("linearGradient", {
        gradientTransform: "matrix(50 0 0 -50.5 12545 22279)",
        gradientUnits: "userSpaceOnUse",
        id: spinnerGradientId,
        x1: "-249.0912",
        x2: "-252.4749",
        y1: "440.3699",
        y2: "439.5264",
        children: [/*#__PURE__*/jsx("stop", {
          offset: ".3052",
          stopColor: getSpinnerColor(theme)
        }), /*#__PURE__*/jsx("stop", {
          offset: ".5",
          stopColor: getSpinnerColor(theme),
          stopOpacity: "0"
        }), _stop || (_stop = /*#__PURE__*/jsx("stop", {
          offset: ".831",
          stopColor: "#000000",
          stopOpacity: "0"
        }))]
      }), /*#__PURE__*/jsx("path", {
        clipRule: "evenodd",
        d: "M50,100A50,50,0,1,0,0,50H4.4A45.6,45.6,0,1,1,50,95.54Z",
        fill: `url(#${spinnerGradientId})`,
        fillRule: "evenodd"
      })]
    }), /*#__PURE__*/jsx(StyledH4, {
      align: "center",
      forwardedAs: "p",
      id: labelId,
      children: children
    })]
  });
}
ProgressSpinner.displayName = 'ProgressSpinner';

export { ProgressBar, ProgressSpinner, progressBarClassName, progressSpinnerClassName };
